import { HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { HttpStatusCode } from '../../shared/constants/http-status-code.enum';
import { HttpResponseContentType } from '../../shared/models/http-response-content-type.enum';
import { DecryptResponseService } from './decrypt-response.service';
import { EncryptedResponse } from './encrypted-response.model';
import { EncryptionService } from './encryption.service';

@Injectable()
export class OfficeDecryptResponseService implements DecryptResponseService {
    constructor(
        private readonly encryptionService: EncryptionService
    ) {
    }

    public async decryptResponse(url: string, encryptedDataBuffer: Uint8Array, bodyFormat: HttpResponseContentType): Promise<HttpResponse<any>> {
        // TODO: encrypted data is only text or json, consider encryption support for Blob and Array Buffer
        if (bodyFormat === HttpResponseContentType.ArrayBuffer || bodyFormat === HttpResponseContentType.Blob) {
            throw this.generateError(HttpStatusCode.NotImplemented, `Unsupported Encrypted Cached Body Type: ${bodyFormat}`);
        }

        let decryptedResponse: EncryptedResponse;
        try {
            decryptedResponse = await this.encryptionService.decryptData<EncryptedResponse>(encryptedDataBuffer);
        } catch (ex) {
            throw this.generateError(HttpStatusCode.DecryptionError, 'Failed to decrypt response.');
        }

        const {
            body: bodyString,
            headers: headersString,
            requestUrl: urlUsedInCache,
            statusText,
            status
        } = decryptedResponse;

        // We check the URL we used to cache the data to make sure that it matches the same URL we are intercepting.
        // This protects against the case where an attacker swaps valid encrypted responses.
        if (!urlUsedInCache || urlUsedInCache.toLowerCase() !== url.toLowerCase()) {
            throw this.generateError(HttpStatusCode.UnexpectedResponse, 'Resource read from cache does not match the resource being used');
        }

        try {
            const body = bodyFormat === HttpResponseContentType.Json ? JSON.parse(bodyString) : bodyString;
            return new HttpResponse<any>({
                body,
                headers: new HttpHeaders(JSON.parse(headersString)),
                statusText,
                status,
                url
            });
        } catch (ex) {
            throw this.generateError(HttpStatusCode.UnexpectedResponse, 'Failed to parse response');
        }
    }

    private generateError(status: HttpStatusCode, statusText: string): HttpErrorResponse {
        return new HttpErrorResponse({
            error: statusText,
            status,
            statusText
        });
    }

}
